import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Sessão iniciada!");
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const quick = (em) => { setEmail(em); setPassword(em.includes("admin") ? "Alliance2026" : "Growth2026"); };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 relative bg-[#0a0b0e] items-center justify-center p-12 border-r border-brand-border overflow-hidden">
        <div className="absolute inset-0 opacity-30"
          style={{ background: "radial-gradient(circle at 30% 30%, rgba(26,86,255,0.35), transparent 50%), radial-gradient(circle at 70% 70%, rgba(255,92,0,0.25), transparent 50%)" }} />
        <div className="relative max-w-md">
          <Logo className="h-12" />
          <h2 className="font-heading font-black text-3xl mt-8 leading-tight">
            Bem-vindo de volta à <span className="text-brand-orange">A4G League</span>.
          </h2>
          <p className="text-brand-muted mt-4">Continue a construir hábitos de excelência e a subir no ranking.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-white mb-8">
            <ArrowLeft size={16} /> Voltar
          </Link>
          <div className="lg:hidden mb-6"><Logo className="h-9" withText /></div>
          <h1 className="font-heading font-bold text-2xl">Iniciar sessão</h1>
          <p className="text-sm text-brand-muted mt-1">Aceda à sua conta Alliance 4 Growth.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="login-email" placeholder="nome@agencia.pt"
                className="mt-1.5 bg-brand-surface border-brand-border rounded-sm h-11" />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm">Palavra-passe</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required data-testid="login-password" placeholder="••••••••"
                className="mt-1.5 bg-brand-surface border-brand-border rounded-sm h-11" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full bg-brand-blue hover:bg-brand-bluehover rounded-sm font-bold h-11">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
            </Button>
          </form>

          <p className="text-sm text-brand-muted mt-6 text-center">
            Não tem conta?{" "}
            <Link to="/registar" className="text-brand-orange font-semibold hover:underline">Registe-se</Link>
          </p>

          <div className="mt-8 p-4 rounded-sm bg-brand-surface border border-brand-border">
            <p className="text-xs text-brand-muted mb-2 font-semibold uppercase tracking-wider">Contas de demonstração</p>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => quick("admin@alliance4growth.com")} data-testid="demo-admin"
                className="text-xs px-2.5 py-1 rounded-sm bg-brand-blue/15 text-brand-blue hover:bg-brand-blue/25">Admin</button>
              <button type="button" onClick={() => quick("master@primeimob.pt")} data-testid="demo-master"
                className="text-xs px-2.5 py-1 rounded-sm bg-white/5 text-white hover:bg-white/10">Master</button>
              <button type="button" onClick={() => quick("gestor@primeimob.pt")} data-testid="demo-manager"
                className="text-xs px-2.5 py-1 rounded-sm bg-white/5 text-white hover:bg-white/10">Broker</button>
              <button type="button" onClick={() => quick("coach@primeimob.pt")} data-testid="demo-coach"
                className="text-xs px-2.5 py-1 rounded-sm bg-white/5 text-white hover:bg-white/10">Coach</button>
              <button type="button" onClick={() => quick("ana@primeimob.pt")} data-testid="demo-consultant"
                className="text-xs px-2.5 py-1 rounded-sm bg-brand-orange/15 text-brand-orange hover:bg-brand-orange/25">Consultor</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
